import React from 'react';
import { View, Text, StyleSheet, Pressable, Platform, ViewStyle } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { Colors, Spacing, Typography } from '../constants';

interface SectionHeaderProps {
  /** @deprecated design v2 section headers carry no icon — accepted and ignored. */
  icon?: keyof typeof Ionicons.glyphMap;
  title: string;
  /** Overline above the title. Falls back to "{{count}} items" when `count` is given. */
  kicker?: string;
  /** Item count, rendered as the default kicker. */
  count?: number;
  /** Text link on the right, rendered as "{action} →". */
  action?: string;
  onAction?: () => void;
  /** Lora 19 instead of 24, and the tighter mobile rhythm. */
  mobile?: boolean;
  style?: ViewStyle;
}

/**
 * Editorial section header (design v2): an overline kicker (usually the count)
 * above a Lora title, a hairline rule under the row, and the action as a text
 * link in accent-strong — no icon, no button. Titles are sentence case.
 */
export const SectionHeader: React.FC<SectionHeaderProps> = ({
  title,
  kicker,
  count,
  action,
  onAction,
  mobile = false,
  style,
}) => {
  const { t } = useTranslation();
  const overline =
    kicker ?? (count != null ? t('components.sectionHeader.count', { count }) : undefined);

  return (
    <View style={[styles.row, mobile && styles.rowMobile, style]}>
      <View style={styles.titleBlock}>
        {overline ? <Text style={styles.kicker}>{overline}</Text> : null}
        <Text
          style={[mobile ? Typography.sectionMobile : Typography.section, styles.title]}
          numberOfLines={1}
        >
          {title}
        </Text>
      </View>
      {action && onAction ? (
        <Pressable
          onPress={onAction}
          accessibilityRole="link"
          accessibilityLabel={action}
          hitSlop={{ top: 12, bottom: 12, left: 8, right: 8 }}
          style={styles.action}
        >
          {({ hovered }: any) => (
            <Text style={[styles.actionText, mobile && styles.actionTextMobile, hovered && styles.actionTextHovered]}>
              {action} →
            </Text>
          )}
        </Pressable>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    gap: Spacing.md,
    paddingBottom: Spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: Colors.borderLight,
    marginBottom: Spacing.lg,
  },
  rowMobile: {
    marginBottom: Spacing.base,
  },
  titleBlock: {
    flexShrink: 1,
    minWidth: 0,
  },
  kicker: {
    ...Typography.overline,
    color: Colors.textLight,
    marginBottom: Spacing.sm,
  },
  title: {
    color: Colors.textDark,
  },
  action: {
    flexShrink: 0,
    ...(Platform.OS === 'web' && { cursor: 'pointer' } as any),
  },
  actionText: {
    ...Typography.link,
    lineHeight: 20,
    color: Colors.accentStrong,
  },
  actionTextMobile: {
    fontSize: 13,
    lineHeight: 18,
  },
  actionTextHovered: {
    color: Colors.textDark,
  },
});
